<template>
  <b-modal
    v-if="docsToRead"
    :id="`ConfirmationReading-${docsToRead.id}`"
    hide-footer
    modal-class="modern-confirmation-modal"
    size="lg"
    :hide-header-close="!onClose"
    :no-close-on-esc="!onClose"
    no-close-on-backdrop
    @show="resetFields"
    @hide="resetFields"
  >
    <template v-slot:modal-title>
      <div class="modal-title-modern">
        <div class="title-icon-container">
          <i class="fas fa-file-alt title-icon"></i>
        </div>
        <div class="title-content">
          <h3 class="title-text">Confirmação de Leitura</h3>
          <p class="title-subtitle">Documento obrigatório para acesso</p>
        </div>
      </div>
    </template>

    <div
      class="confirmation-content"
      ref="modalScroll"
      @scroll="checkScrollEnd"
    >
      <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
        <div class="document-card">
          <div class="document-header">
            <div class="document-icon">
              <i class="fas fa-file-contract"></i>
            </div>

            <div class="document-info">
              <h4 class="document-title">{{ docsToRead.doc_name }}</h4>

              <span class="document-label">Documento obrigatório</span>
            </div>

            <div class="document-status">
              <div class="status-badge status-pending">
                <i class="fas fa-clock mr-1"></i>
                Pendente
              </div>
            </div>
          </div>

          <p class="simple-label">Desça até o final para visualizar</p>

          <div class="document-actions">
            <div v-if="docsToRead.doc_url">
              <div v-show="isPdf">
                <iframe
                  id="iframefolderscroll"
                  :src="docsToRead.doc_url"
                  width="100%"
                  height="1050px"
                  style="border: none"
                  ref="iframefolderscroll"
                ></iframe>
              </div>

              <div v-show="!isPdf">
                <img
                  :src="docsToRead.doc_url"
                  alt="Documento"
                  style="
                    max-width: 100%;
                    max-height: 900px;
                    display: block;
                    margin: 0 auto;
                  "
                />
              </div>
            </div>
          </div>

          <div class="skip-counter">
            <div class="counter-info">
              <i class="fas fa-info-circle counter-icon"></i>

              <div class="counter-details">
                <span class="counter-label">Tentativas de pular restantes</span>

                <div class="counter-progress">
                  <div class="progress-bar">
                    <div
                      class="progress-fill"
                      :style="{
                        width:
                          (docsToRead.attempts / docsToRead.doc_attempts) *
                            100 +
                          '%',
                      }"
                    ></div>
                  </div>

                  <span class="counter-text">
                    {{ docsToRead.attempts }} /
                    {{ docsToRead.doc_attempts }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="confirmation-section">
          <div class="section-header">
            <h4 class="section-title">
              <i class="fas fa-check-circle mr-2"></i>
              Confirmar Leitura
            </h4>

            <p class="section-subtitle">
              Você leu e compreendeu todo o conteúdo do documento?
            </p>
          </div>

          <div class="toggle-section">
            <div class="toggle-label">Marcar como lido?</div>

            <div class="toggle-buttons">
              <button
                type="button"
                class="toggle-btn"
                :class="{ active: !confirmation_read }"
                @click="confirmation_read = false"
                :disabled="!scrollEnd"
              >
                <i class="fas fa-times mr-2"></i>
                Não
              </button>

              <button
                type="button"
                class="toggle-btn"
                :class="{ active: confirmation_read }"
                @click="confirmation_read = true"
                :disabled="!scrollEnd"
              >
                <i class="fas fa-check mr-2"></i>
                Sim
              </button>
            </div>
          </div>

          <div v-if="confirmation_read" class="confirmation-input-section">
            <div class="input-group">
              <LabelRequiredField label="Para confirmar, digite exatamente:" />

              <div class="confirmation-target">
                <code class="target-text">Eu li o documento</code>
              </div>

              <div class="input-container">
                <input
                  type="text"
                  v-model="confirmation"
                  class="confirmation-input"
                  placeholder="Digite a confirmação aqui..."
                  :class="{
                    'input-valid': confirmation === 'Eu li o documento',
                    'input-invalid':
                      confirmation !== '' &&
                      confirmation !== 'Eu li o documento',
                  }"
                />

                <div class="input-feedback">
                  <i
                    v-if="confirmation === 'Eu li o documento'"
                    class="fas fa-check-circle feedback-icon valid"
                  ></i>

                  <i
                    v-else-if="confirmation !== ''"
                    class="fas fa-times-circle feedback-icon invalid"
                  ></i>
                </div>
              </div>

              <div
                v-if="
                  confirmation !== '' && confirmation !== 'Eu li o documento'
                "
                class="input-help"
              >
                <i class="fas fa-exclamation-triangle mr-1"></i>
                Digite exatamente: "Eu li o documento"
              </div>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="errors.length > 0" class="errors-section">
          <ErrorsMessages :errors="errors" />
        </div>

        <div class="action-buttons">
          <v-btn
            v-if="confirmation_read"
            @click="readConfirmation"
            class="btn-confirm-reading"
            large
            :disabled="confirmation !== 'Eu li o documento' || loading"
          >
            <i class="fas fa-check-double mr-2"></i>
            {{ loading ? "Confirmando..." : "Confirmar Leitura" }}
          </v-btn>

          <v-btn
            v-if="
              !confirmation_read &&
              docsToRead.attempts < docsToRead.doc_attempts
            "
            @click="skipConfirmation"
            class="btn-skip-reading"
            large
            :disabled="loading || !scrollEnd"
          >
            <i class="fas fa-forward mr-2"></i>
            {{ loading ? "Pulando..." : "Pular por Agora" }}
          </v-btn>

          <div
            v-if="
              !confirmation_read &&
              docsToRead.attempts >= docsToRead.doc_attempts
            "
            class="no-skips-message"
          >
            <div class="message-content">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              <span
                >Não é mais possível pular. Você deve ler o documento para
                continuar.</span
              >
            </div>
          </div>
        </div>
      </b-overlay>
    </div>
  </b-modal>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages.vue";

import LabelRequiredField from "../Components/LabelRequiredField";

import api from "../../services/api";

import toast from "../../lib/ToastNotification";

export default {
  name: "ConfirmationReading",

  components: {
    LabelRequiredField,
    ErrorsMessages,
  },

  props: {
    docsToRead: {
      type: [Array, Object],
      default: () => ({}),
    },

    onClose: Boolean,
  },

  data() {
    return {
      confirmation: "",

      errors: [],

      confirmation_read: false,

      check_read: false,

      loading: false,

      scrollEnd: false,
    };
  },

  computed: {
    isPdf() {
      return /\.pdf$/i.test(this.docsToRead.doc_url || "");
    },
  },

  watch: {
    confirmation_read() {
      this.errors = [];

      this.confirmation = "";
    },
  },

  mounted() {
    this.$nextTick(() => {
      const modalScrollElement = this.$refs.modalScroll;
      if (modalScrollElement) modalScrollElement.scrollTop = 0;
    });
  },

  methods: {
    async readConfirmation() {
      try {
        this.loading = true;

        const id = this.docsToRead.doc_id;

        const { confirmation } = this;

        let params = { id, confirmation };

        const response = await api.put("/folder/user/read-doc", params);

        const { error, message, errors } = response.data;

        if (!error) {
          this.errors = [];

          toast(message, "success");

          this.$emit("reload");

          this.$bvModal.hide("ConfirmationReading");
        } else {
          this.errors = errors;
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;

        console.log(error);
      }
    },

    async skipConfirmation() {
      try {
        this.loading = true;

        const id = this.docsToRead.doc_id;

        let params = { id };

        const response = await api.put("/folder/user/read-doc/skip", params);

        const { error, message, errors } = response.data;

        if (!error) {
          this.errors = [];

          toast(message, "success");

          this.$emit("reload");

          this.$bvModal.hide("ConfirmationReading");
        } else {
          this.errors = errors;
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },

    checkScrollEnd() {
      const modalScrollElement = this.$refs.modalScroll;
      if (modalScrollElement) {
        if (
          modalScrollElement.scrollTop + modalScrollElement.clientHeight >=
          modalScrollElement.scrollHeight - 2
        ) {
          this.scrollEnd = true;
        } else {
          this.scrollEnd = false;
        }
      }
    },

    resetFields() {
      this.confirmation_read = false;

      this.check_read = false;

      this.confirmation = "";

      this.errors = [];

      this.loading = false;

      this.scrollEnd = false;
    },
  },
};
</script>

<style scoped>
/* Modal Styling */
.confirmation-content {
  padding: 0;
  max-height: 70vh; /* ou ajuste conforme necessário */
  overflow-y: auto;
}

.simple-label {
  text-align: center;
  color: #1e40af;
  font-weight: 600;
  font-size: 13px;
  margin: 18px 0 8px 0;
  letter-spacing: 0.5px;
}

.loading-overlay {
  border-radius: 12px;
  min-height: 200px;
}

/* Modal Title */
.modal-title-modern {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 0;
}

.title-icon-container {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title-icon {
  color: white;
  font-size: 20px;
}

.title-content {
  flex: 1;
}

.title-text {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.title-subtitle {
  color: #d3d3d3;
  font-size: 14px;
  margin: 2px 0 0 0;
}

/* Document Card */
.document-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.document-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-bottom: 1px solid #e5e7eb;
}

.document-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.document-info {
  flex: 1;
}

.document-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  word-break: break-word;
}

.document-label {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #d97706;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid #f59e0b;
}

.document-status {
  display: flex;
  align-items: center;
}

.status-badge {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-pending {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

/* Document Actions */
.document-actions {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.view-document-btn {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 14px 28px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3) !important;
  transition: all 0.3s ease !important;
  width: 100% !important;
}

.view-document-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(15, 101, 176, 0.4) !important;
}

/* Skip Counter */
.skip-counter {
  padding: 20px 24px;
}

.counter-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.counter-icon {
  color: #0f65b0;
  font-size: 16px;
  margin-top: 2px;
}

.counter-details {
  flex: 1;
}

.counter-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.counter-progress {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.counter-text {
  font-size: 12px;
  font-weight: 600;
  color: #0f65b0;
  min-width: 40px;
}

/* Confirmation Section */
.confirmation-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.section-header {
  margin-bottom: 24px;
  text-align: center;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-title i {
  color: #0f65b0;
}

.section-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Toggle Section */
.toggle-section {
  margin-bottom: 24px;
}

.toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
  text-align: center;
}

.toggle-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.toggle-btn {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  color: #64748b;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
  justify-content: center;
}

.toggle-btn:hover {
  border-color: #0f65b0;
  color: #0f65b0;
  transform: translateY(-1px);
}

.toggle-btn.active {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-color: #0f65b0;
  color: white;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3);
}

.toggle-btn:disabled {
  background: #f3f4f6 !important;
  color: #b0b0b0 !important;
  border-color: #e5e7eb !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
  opacity: 1 !important;
}

/* Confirmation Input */
.confirmation-input-section {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.confirmation-target {
  background: #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  border-left: 4px solid #0f65b0;
}

.target-text {
  background: none;
  color: #1e293b;
  font-weight: 600;
  font-size: 14px;
}

.input-container {
  position: relative;
}

.confirmation-input {
  width: 100%;
  padding: 14px 48px 14px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.confirmation-input:focus {
  outline: none;
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

.confirmation-input.input-valid {
  border-color: #10b981;
  background: #ecfdf5;
}

.confirmation-input.input-invalid {
  border-color: #ef4444;
  background: #fef2f2;
}

.input-feedback {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.feedback-icon {
  font-size: 16px;
}

.feedback-icon.valid {
  color: #10b981;
}

.feedback-icon.invalid {
  color: #ef4444;
}

.input-help {
  color: #ef4444;
  font-size: 12px;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #fef2f2;
  border-radius: 6px;
  border: 1px solid #fca5a5;
}

/* Error Section */
.errors-section {
  margin-bottom: 24px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.btn-confirm-reading {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 16px 32px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
  width: 100% !important;
}

.btn-confirm-reading:hover:not(:disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4) !important;
}

.btn-confirm-reading:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

.btn-skip-reading {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 16px 32px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3) !important;
  transition: all 0.3s ease !important;
  width: 100% !important;
}

.btn-skip-reading:hover:not(:disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4) !important;
}

.btn-skip-reading:disabled {
  background: #f3f4f6 !important;
  color: #b0b0b0 !important;
  border: 1px solid #e5e7eb !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
}

/* No Skips Message */
.no-skips-message {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  border: 1px solid #f59e0b;
  border-radius: 12px;
  padding: 20px;
}

.message-content {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #92400e;
  font-weight: 500;
  text-align: center;
}

/* Modal Overrides */
.modern-confirmation-modal >>> .modal-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-bottom: 1px solid #e5e7eb;
  border-radius: 0;
  padding: 24px;
}

.modern-confirmation-modal >>> .modal-body {
  padding: 24px;
  background: #f8fafc;
}

.modern-confirmation-modal >>> .modal-content {
  border-radius: 16px;
  border: none;
  box-shadow: 0 20px 64px rgba(0, 0, 0, 0.15);
}

/* Responsive Design */
@media (max-width: 768px) {
  .document-header {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .document-info {
    text-align: center;
  }

  .toggle-buttons {
    flex-direction: column;
  }

  .toggle-btn {
    min-width: auto;
    width: 100%;
  }

  .counter-progress {
    flex-direction: column;
    gap: 8px;
  }

  .counter-text {
    min-width: auto;
    text-align: center;
  }

  .modern-confirmation-modal >>> .modal-body {
    padding: 16px;
  }

  .confirmation-section,
  .document-card {
    margin-bottom: 16px;
  }
}

@media (max-width: 480px) {
  .modal-title-modern {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .title-icon-container {
    width: 40px;
    height: 40px;
  }

  .title-icon {
    font-size: 18px;
  }

  .title-text {
    font-size: 18px;
  }

  .document-header {
    padding: 16px;
  }

  .document-icon {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .document-title {
    font-size: 16px;
  }

  .confirmation-section {
    padding: 16px;
  }

  .toggle-btn {
    padding: 10px 20px;
    font-size: 13px;
  }
}
</style>